/**
 * Mezzo proprio — anagrafica dei mezzi personali usati per servizio, e autorizzazione (BE v1.100.0).
 *
 * **Perché una pagina sola per me e per il team.** Il BE restringe già `erp-employee-vehicles` al
 * perimetro «io ∪ sottoposti diretti ∪ tenant per chi ha il claim org-wide»: una sola GET torna
 * esattamente ciò che si può vedere. Dividerla qui in due schede è una lettura di quel risultato,
 * non un secondo giro di filtri — e chi non è responsabile di nessuno vede semplicemente una scheda
 * vuota di zero elementi, che nascondiamo.
 *
 * **L'autorizzazione non è un campo come gli altri.** `isAuthorized` lo alza il responsabile, mai
 * l'interessato: il BE risponde `403 OWN_VEHICLE_AUTHORIZATION_FORBIDDEN` anche a chi ha il claim di
 * update sul proprio mezzo, perché un'autorizzazione che uno si dà da sé non attribuisce
 * responsabilità a nessuno in caso di sinistro. Qui l'interruttore non compare proprio sui propri
 * mezzi — mostrarlo per poi rifiutarlo sarebbe una promessa fatta e ritirata.
 */
import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import {
  ActionIcon, Badge, Button, Group, Select, SimpleGrid, Switch, Table, Text, Textarea,
  TextInput, Tooltip,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { modals } from '@mantine/modals';
import {
  IconAlertTriangle, IconCar, IconClock, IconPencil, IconPlus, IconShieldCheck, IconTrash,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useAbility } from '@/app/providers/AbilityProvider';
import { MaeCard, MaeField, MaeFormSection, MaeModal, MaePageHead, MaeShell, MaeSubTabs } from '@/shared/desktop/MaeShell';
import { maeTableStyles } from '@/shared/desktop/MaeShell.utils';
import { StatCard } from '@/shared/components/StatCard';
import { MaeAsync } from '@/shared/components/MaeAsync';
import { MaeAdvice } from '@/shared/components/MaeAdvice';
import { Paginated } from '@/shared/components/DataTable/TablePagination';
import { useCreateResource, useDeleteResource, useResourceList, useUpdateResource } from '@/shared/api/jsonapi/hooks';
import { notifyError, notifySuccess, getErrorMessage } from '@/shared/lib/notify';
import { useMyEmployeeId } from '@/features/workforce/useMyEmployee';
import {
  FUEL_TYPE_OPTIONS, VEHICLE_CATEGORY_OPTIONS, canAuthorizeVehicle, fuelTypeLabel, insuranceStatus,
  vehicleCategoryLabel, vehicleLabel, type EmployeeVehicleRow,
} from '@/features/fleet/ownVehicle';

export const Route = createFileRoute('/_app/my-vehicles')({ component: MyVehiclesPage });

interface VehicleForm {
  licensePlate: string;
  brand: string;
  model: string;
  fuelType: string | null;
  vehicleCategory: string | null;
  insuranceExpiryDate: Date | null;
  isActive: boolean;
  notes: string;
}

const EMPTY: VehicleForm = {
  licensePlate: '', brand: '', model: '', fuelType: null, vehicleCategory: null,
  insuranceExpiryDate: null, isActive: true, notes: '',
};

function MyVehiclesPage() {
  const ability = useAbility();
  const myEmployeeId = useMyEmployeeId();
  const [tab, setTab] = useState<'mine' | 'team'>('mine');
  const [editing, setEditing] = useState<EmployeeVehicleRow | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const listQ = useResourceList<EmployeeVehicleRow>('erp-employee-vehicles', {
    include: ['employee', 'authorizedByEmployee'],
    sort: ['-createdAt'],
    page: { size: 100 },
  });

  const create = useCreateResource<EmployeeVehicleRow>('erp-employee-vehicles');
  const update = useUpdateResource<EmployeeVehicleRow>('erp-employee-vehicles');
  const remove = useDeleteResource('erp-employee-vehicles');

  const rows = useMemo(() => listQ.data?.data ?? [], [listQ.data]);
  const { mine, team } = useMemo(() => {
    const mineRows: EmployeeVehicleRow[] = [];
    const teamRows: EmployeeVehicleRow[] = [];
    for (const v of rows) {
      // Senza `myEmployeeId` (utente non collegato a un dipendente) tutto finisce fra i "miei":
      // meglio mostrarli lì che farli sparire da entrambe le schede.
      if (!myEmployeeId || !v.employee?.id || v.employee.id === myEmployeeId) mineRows.push(v);
      else teamRows.push(v);
    }
    return { mine: mineRows, team: teamRows };
  }, [rows, myEmployeeId]);

  const shown = tab === 'mine' ? mine : team;
  const canWrite = ability.can('create', 'ErpEmployeeVehicle');
  const canUpdate = ability.can('update', 'ErpEmployeeVehicle');
  const canDelete = ability.can('delete', 'ErpEmployeeVehicle');
  // La primitiva di approvazione del BE lascia passare superadmin/HR/see-all **prima** del
  // controllo sull'owner: per loro il 403 non arriva, quindi l'interruttore va mostrato anche sul
  // proprio mezzo — verificato sul dev.
  const bypassesOwnerCheck = ability.can('readAll', 'TimesheetEntry') || ability.can('manage', 'all');

  const pendingCount = team.filter((v) => !v.isAuthorized && v.isActive !== false).length;
  const expiringCount = rows.filter((v) => {
    const s = insuranceStatus(v.insuranceExpiryDate);
    return s.level === 'expired' || s.level === 'expiring';
  }).length;

  const form = useForm<VehicleForm>({
    initialValues: EMPTY,
    validate: {
      licensePlate: (v) => (v.trim() ? null : 'La targa è obbligatoria'),
    },
  });

  function openCreate() {
    setEditing(null);
    form.setValues(EMPTY);
    form.resetDirty(EMPTY);
    setModalOpen(true);
  }

  function openEdit(v: EmployeeVehicleRow) {
    setEditing(v);
    const values: VehicleForm = {
      licensePlate: v.licensePlate ?? '',
      brand: v.brand ?? '',
      model: v.model ?? '',
      fuelType: v.fuelType ?? null,
      vehicleCategory: v.vehicleCategory ?? null,
      insuranceExpiryDate: v.insuranceExpiryDate ? dayjs(v.insuranceExpiryDate).toDate() : null,
      isActive: v.isActive !== false,
      notes: v.notes ?? '',
    };
    form.setValues(values);
    form.resetDirty(values);
    setModalOpen(true);
  }

  async function submit(values: VehicleForm) {
    const attributes = {
      licensePlate: values.licensePlate.trim().toUpperCase(),
      brand: values.brand.trim() || null,
      model: values.model.trim() || null,
      fuelType: values.fuelType,
      vehicleCategory: values.vehicleCategory,
      insuranceExpiryDate: values.insuranceExpiryDate ? dayjs(values.insuranceExpiryDate).format('YYYY-MM-DD') : null,
      isActive: values.isActive,
      notes: values.notes.trim() || null,
    };
    try {
      if (editing) {
        await update.mutateAsync({ id: editing.id, attributes });
        notifySuccess('Mezzo aggiornato');
      } else {
        // In creazione il proprietario è chi scrive: il BE non lo deduce, e senza relationship il
        // mezzo resterebbe orfano e invisibile al perimetro di lettura.
        await create.mutateAsync({
          attributes,
          ...(myEmployeeId
            ? { relationships: { employee: { data: { type: 'erp-employees', id: myEmployeeId } } } }
            : {}),
        });
        notifySuccess('Mezzo aggiunto');
      }
      setModalOpen(false);
    } catch (e) {
      notifyError(getErrorMessage(e));
    }
  }

  async function toggleAuthorization(v: EmployeeVehicleRow, next: boolean) {
    try {
      await update.mutateAsync({ id: v.id, attributes: { isAuthorized: next } });
      notifySuccess(next ? 'Mezzo autorizzato all\'uso per servizio' : 'Autorizzazione revocata');
    } catch (e) {
      notifyError(getErrorMessage(e));
    }
  }

  function confirmDelete(v: EmployeeVehicleRow) {
    modals.openConfirmModal({
      title: 'Elimina mezzo',
      children: (
        <Text size="sm">
          Elimini <strong>{vehicleLabel(v)}</strong>? Le rendicontazioni già registrate con questo mezzo
          restano, ma non potrai più selezionarlo.
        </Text>
      ),
      labels: { confirm: 'Elimina', cancel: 'Annulla' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await remove.mutateAsync(v.id);
          notifySuccess('Mezzo eliminato');
        } catch (e) {
          notifyError(getErrorMessage(e));
        }
      },
    });
  }

  return (
    <MaeShell>
      <MaePageHead
        title="Mezzo proprio"
        sub="I mezzi personali che usi per servizio e la loro autorizzazione"
        icon={<IconCar size={23} />}
        actions={canWrite ? <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>Aggiungi mezzo</Button> : undefined}
      />

      <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md" mb="lg">
        <StatCard label="I miei mezzi" value={mine.length} icon={IconCar} color="dark" />
        <StatCard label="Da autorizzare" value={pendingCount} icon={IconClock} color={pendingCount > 0 ? 'orange' : 'gray'} />
        <StatCard label="Assicurazioni in scadenza" value={expiringCount} icon={IconAlertTriangle} color={expiringCount > 0 ? 'red' : 'gray'} />
      </SimpleGrid>

      {team.length > 0 && (
        <MaeSubTabs
          value={tab}
          onChange={(v) => setTab(v as 'mine' | 'team')}
          tabs={[
            { value: 'mine', label: 'I miei mezzi', count: mine.length },
            { value: 'team', label: 'Del team', count: team.length },
          ]}
        />
      )}

      <MaeAsync
        query={listQ}
        loading="Caricamento dei mezzi…"
        empty={shown.length === 0 && {
          icon: IconCar,
          title: tab === 'mine' ? 'Nessun mezzo censito' : 'Nessun mezzo dei tuoi collaboratori',
          description: tab === 'mine'
            ? 'Aggiungi la tua auto per poter indicare «mezzo proprio» in rendicontazione e ricevere il rimborso chilometrico.'
            : 'Quando un collaboratore censisce il proprio mezzo lo trovi qui per autorizzarlo.',
          ...(canWrite && tab === 'mine' ? { action: { label: 'Aggiungi mezzo', onClick: openCreate } } : {}),
        }}
      >
        {tab === 'mine' && mine.some((v) => !v.isAuthorized && v.isActive !== false) && (
          <div style={{ marginBottom: 16 }}>
            <MaeAdvice
              tone="warning"
              title="Un mezzo è in attesa di autorizzazione"
              selfRemedy="L'uso dell'auto privata per servizio lo autorizza il tuo responsabile: chiedi a lui di approvarlo da questa stessa pagina."
            >
              Finché l'autorizzazione manca, il mezzo resta selezionabile solo in grigio in rendicontazione e
              non produce rimborso chilometrico.
            </MaeAdvice>
          </div>
        )}

        <MaeCard padding={0}>
          <Paginated rows={shown} pageSize={15} label="mezzi" barPad={16}>
            {(page) => (
              <Table.ScrollContainer minWidth={860}>
                <Table striped highlightOnHover verticalSpacing="sm" styles={maeTableStyles}>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Mezzo</Table.Th>
                      {tab === 'team' && <Table.Th>Dipendente</Table.Th>}
                      <Table.Th>Fascia</Table.Th>
                      <Table.Th>Alimentazione</Table.Th>
                      <Table.Th>Assicurazione</Table.Th>
                      <Table.Th>Autorizzazione</Table.Th>
                      <Table.Th w={90} />
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {page.map((v) => (
                      <VehicleRow
                        key={v.id}
                        vehicle={v}
                        showEmployee={tab === 'team'}
                        canToggleAuth={canAuthorizeVehicle(v, myEmployeeId, canUpdate, bypassesOwnerCheck)}
                        canEdit={canUpdate}
                        canDelete={canDelete}
                        busy={update.isPending}
                        onEdit={() => openEdit(v)}
                        onDelete={() => confirmDelete(v)}
                        onToggleAuth={(next) => toggleAuthorization(v, next)}
                      />
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            )}
          </Paginated>
        </MaeCard>
      </MaeAsync>

      <MaeModal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Modifica mezzo' : 'Aggiungi mezzo'}
        icon={<IconCar size={17} />}
        footer={
          <Button onClick={() => form.onSubmit(submit)()} loading={create.isPending || update.isPending}>
            {editing ? 'Salva' : 'Aggiungi'}
          </Button>
        }
      >
        <MaeFormSection title="Identificazione" sub="La targa è l'unica cosa che rende il rimborso giustificabile in caso di verifica.">
          <MaeField label="Targa" required>
            <TextInput
              placeholder="AB123CD"
              {...form.getInputProps('licensePlate')}
              onChange={(e) => form.setFieldValue('licensePlate', e.currentTarget.value.toUpperCase())}
            />
          </MaeField>
          <Group grow>
            <MaeField label="Marca">
              <TextInput placeholder="es. Fiat" {...form.getInputProps('brand')} />
            </MaeField>
            <MaeField label="Modello">
              <TextInput placeholder="es. Panda" {...form.getInputProps('model')} />
            </MaeField>
          </Group>
        </MaeFormSection>

        <MaeFormSection title="Caratteristiche" sub="La fascia decide quale tariffa chilometrica ti viene applicata quando non ce n'è una per il tuo mezzo.">
          <Group grow>
            <MaeField label="Fascia">
              <Select
                placeholder="Seleziona…"
                data={[...VEHICLE_CATEGORY_OPTIONS]}
                clearable
                {...form.getInputProps('vehicleCategory')}
              />
            </MaeField>
            <MaeField label="Alimentazione">
              <Select
                placeholder="Seleziona…"
                data={[...FUEL_TYPE_OPTIONS]}
                clearable
                {...form.getInputProps('fuelType')}
              />
            </MaeField>
          </Group>
          <MaeField
            label="Scadenza assicurazione"
            hint="Nessun promemoria automatico: te lo segnaliamo qui e quando scegli il mezzo in rendicontazione."
          >
            <DatePickerInput
              placeholder="Seleziona data…"
              valueFormat="DD/MM/YYYY"
              clearable
              {...form.getInputProps('insuranceExpiryDate')}
            />
          </MaeField>
        </MaeFormSection>

        <MaeFormSection title="Altro">
          <MaeField label="Note">
            <Textarea autosize minRows={2} placeholder="Annotazioni sul mezzo…" {...form.getInputProps('notes')} />
          </MaeField>
          <Switch
            label="Mezzo attivo"
            description="Disattivalo quando non lo usi più: resta nello storico ma non è più selezionabile."
            checked={form.values.isActive}
            onChange={(e) => form.setFieldValue('isActive', e.currentTarget.checked)}
          />
        </MaeFormSection>
      </MaeModal>
    </MaeShell>
  );
}

function VehicleRow({
  vehicle, showEmployee, canToggleAuth, canEdit, canDelete, busy, onEdit, onDelete, onToggleAuth,
}: {
  vehicle: EmployeeVehicleRow;
  showEmployee: boolean;
  canToggleAuth: boolean;
  canEdit: boolean;
  canDelete: boolean;
  busy: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onToggleAuth: (next: boolean) => void;
}) {
  const ins = insuranceStatus(vehicle.insuranceExpiryDate);
  const owner = vehicle.employee;
  return (
    <Table.Tr style={vehicle.isActive === false ? { opacity: 0.55 } : undefined}>
      <Table.Td>
        <Text fw={700} size="sm" style={{ letterSpacing: '0.03em' }}>{vehicle.licensePlate ?? '—'}</Text>
        <Text size="xs" c="dimmed">
          {[vehicle.brand, vehicle.model].filter(Boolean).join(' ') || '—'}
          {vehicle.isActive === false && ' · non attivo'}
        </Text>
      </Table.Td>
      {showEmployee && (
        <Table.Td>
          <Text size="sm">{[owner?.firstName, owner?.lastName].filter(Boolean).join(' ') || '—'}</Text>
        </Table.Td>
      )}
      <Table.Td><Text size="sm">{vehicleCategoryLabel(vehicle.vehicleCategory)}</Text></Table.Td>
      <Table.Td><Text size="sm">{fuelTypeLabel(vehicle.fuelType)}</Text></Table.Td>
      <Table.Td>
        {ins.level === 'unknown' && <Text size="sm" c="dimmed">Non indicata</Text>}
        {ins.level === 'ok' && <Text size="sm">{dayjs(vehicle.insuranceExpiryDate).format('DD/MM/YYYY')}</Text>}
        {(ins.level === 'expiring' || ins.level === 'expired') && (
          <Tooltip label={ins.message} multiline w={240}>
            <Badge
              size="sm"
              variant="light"
              color={ins.level === 'expired' ? 'red' : 'orange'}
              leftSection={<IconAlertTriangle size={11} />}
            >
              {dayjs(vehicle.insuranceExpiryDate).format('DD/MM/YYYY')}
            </Badge>
          </Tooltip>
        )}
      </Table.Td>
      <Table.Td>
        {canToggleAuth ? (
          <Switch
            size="sm"
            checked={!!vehicle.isAuthorized}
            disabled={busy}
            onChange={(e) => onToggleAuth(e.currentTarget.checked)}
            aria-label={`Autorizza ${vehicleLabel(vehicle)}`}
          />
        ) : vehicle.isAuthorized ? (
          <Tooltip
            label={
              vehicle.authorizedAt
                ? `Autorizzato il ${dayjs(vehicle.authorizedAt).format('DD/MM/YYYY')}`
                : 'Autorizzato'
            }
          >
            <Badge size="sm" variant="light" color="green" leftSection={<IconShieldCheck size={11} />}>
              Autorizzato
            </Badge>
          </Tooltip>
        ) : (
          <Tooltip label="L'autorizzazione la concede il tuo responsabile, non tu.">
            <Badge size="sm" variant="light" color="gray" leftSection={<IconClock size={11} />}>
              In attesa
            </Badge>
          </Tooltip>
        )}
      </Table.Td>
      <Table.Td>
        <Group gap={4} justify="flex-end" wrap="nowrap">
          {canEdit && (
            <Tooltip label="Modifica">
              <ActionIcon variant="subtle" color="gray" onClick={onEdit} aria-label="Modifica mezzo">
                <IconPencil size={16} />
              </ActionIcon>
            </Tooltip>
          )}
          {canDelete && (
            <Tooltip label="Elimina">
              <ActionIcon variant="subtle" color="red" onClick={onDelete} aria-label="Elimina mezzo">
                <IconTrash size={16} />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>
      </Table.Td>
    </Table.Tr>
  );
}
